import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { warehouseAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Plus, Search, Edit, Trash2, RotateCcw, Warehouse, MapPin,
  Phone, Mail, User
} from 'lucide-react';

const WarehouseMaster = () => {
  const { toast } = useToast();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const emptyForm = {
    warehouse_id: '', name: '',
    street: '', street2: '', city: '', state: '', zip_code: '', country: 'US',
    contact_name: '', contact_phone: '', contact_email: '',
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const data = await warehouseAPI.getAll(showInactive);
      setWarehouses(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load warehouses', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [showInactive, toast]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm(emptyForm);

  const handleField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const formToPayload = () => ({
    warehouse_id: form.warehouse_id,
    name: form.name,
    address: {
      street: form.street, street2: form.street2,
      city: form.city, state: form.state,
      zip_code: form.zip_code, country: form.country,
    },
    contact: {
      name: form.contact_name,
      phone: form.contact_phone,
      email: form.contact_email,
    },
  });

  const handleCreate = async () => {
    if (!form.warehouse_id || !form.name) {
      toast({ title: 'Required', description: 'Warehouse ID and Name are required', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      await warehouseAPI.create(formToPayload());
      toast({ title: 'Created', description: `Warehouse ${form.warehouse_id} created` });
      setShowAdd(false);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.name) {
      toast({ title: 'Required', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setFormLoading(true);
    try {
      const payload = formToPayload();
      delete payload.warehouse_id;
      await warehouseAPI.update(showEdit, payload);
      toast({ title: 'Updated', description: `Warehouse ${showEdit} updated` });
      setShowEdit(null);
      resetForm();
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await warehouseAPI.delete(showDeleteConfirm);
      toast({ title: 'Deleted', description: `Warehouse ${showDeleteConfirm} removed` });
      setShowDeleteConfirm(null);
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleRestore = async (wid) => {
    try {
      await warehouseAPI.restore(wid);
      toast({ title: 'Restored', description: `Warehouse ${wid} restored` });
      load();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to restore', variant: 'destructive' });
    }
  };

  const openEdit = (wh) => {
    const addr = wh.address || {};
    const contact = wh.contact || {};
    setForm({
      warehouse_id: wh.warehouse_id,
      name: wh.name,
      street: addr.street || '', street2: addr.street2 || '',
      city: addr.city || '', state: addr.state || '',
      zip_code: addr.zip_code || '', country: addr.country || 'US',
      contact_name: contact.name || '',
      contact_phone: contact.phone || '',
      contact_email: contact.email || '',
    });
    setShowEdit(wh.warehouse_id);
  };

  const formatAddress = (addr) => {
    if (!addr) return '-';
    const parts = [addr.street, addr.city, addr.state, addr.zip_code].filter(Boolean);
    return parts.join(', ') || '-';
  };

  const filtered = warehouses.filter(wh => {
    if (!search) return true;
    const q = search.toLowerCase();
    return wh.warehouse_id.toLowerCase().includes(q) ||
      wh.name.toLowerCase().includes(q) ||
      formatAddress(wh.address).toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
        </div>
      </Layout>
    );
  }

  // Inline address fields (NOT a sub-component — prevents remount on keystrokes)
  const addressFields = (
    <>
      <div className="space-y-1.5">
        <Label className="text-xs">Street Address</Label>
        <Input value={form.street} placeholder="123 Main St"
          onChange={(e) => handleField('street', e.target.value)}
          data-testid="form-street" className="h-8 text-sm" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Street Line 2</Label>
        <Input value={form.street2} placeholder="Suite, Unit, etc."
          onChange={(e) => handleField('street2', e.target.value)}
          data-testid="form-street2" className="h-8 text-sm" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">City</Label>
          <Input value={form.city} placeholder="City"
            onChange={(e) => handleField('city', e.target.value)}
            data-testid="form-city" className="h-8 text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">State</Label>
          <Input value={form.state} placeholder="State"
            onChange={(e) => handleField('state', e.target.value)}
            data-testid="form-state" className="h-8 text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">ZIP Code</Label>
          <Input value={form.zip_code} placeholder="ZIP"
            onChange={(e) => handleField('zip_code', e.target.value)}
            data-testid="form-zip" className="h-8 text-sm" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Country</Label>
        <Input value={form.country} placeholder="Country"
          onChange={(e) => handleField('country', e.target.value)}
          data-testid="form-country" className="h-8 text-sm" />
      </div>
    </>
  );

  const contactFields = (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div className="space-y-1.5">
        <Label className="text-xs">Contact Name</Label>
        <Input value={form.contact_name} placeholder="Full Name"
          onChange={(e) => handleField('contact_name', e.target.value)}
          data-testid="form-contact-name" className="h-8 text-sm" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Phone</Label>
        <Input value={form.contact_phone} placeholder="555-123-4567"
          onChange={(e) => handleField('contact_phone', e.target.value)}
          data-testid="form-contact-phone" className="h-8 text-sm" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Email</Label>
        <Input value={form.contact_email} placeholder="email@example.com"
          onChange={(e) => handleField('contact_email', e.target.value)}
          data-testid="form-contact-email" className="h-8 text-sm" />
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-4" data-testid="warehouse-master-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
              <Warehouse className="h-6 w-6 text-sky-600" /> Warehouse Master Data
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{filtered.length} warehouses</p>
          </div>
          <Button size="sm" onClick={() => { resetForm(); setShowAdd(true); }}
            className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            data-testid="add-warehouse-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Warehouse
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search by ID, name, address..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-sm" data-testid="search-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300" data-testid="show-inactive-checkbox" />
            Show inactive
          </label>
        </div>

        {/* Compact Table */}
        <div className="bg-white rounded-xl border shadow-sm overflow-x-auto" data-testid="warehouses-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-2.5 text-left">ID</th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Street</th>
                <th className="px-3 py-2.5 text-left">City</th>
                <th className="px-3 py-2.5 text-left">State</th>
                <th className="px-3 py-2.5 text-left">ZIP</th>
                <th className="px-3 py-2.5 text-left">Contact</th>
                <th className="px-3 py-2.5 text-left">Phone</th>
                <th className="px-3 py-2.5 text-left">Email</th>
                <th className="px-3 py-2.5 text-center w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-gray-400">
                    <Warehouse className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="font-medium text-sm">No warehouses found</p>
                  </td>
                </tr>
              ) : (
                filtered.map(wh => {
                  const isInactive = !wh.is_active;
                  const addr = wh.address || {};
                  const contact = wh.contact || {};
                  return (
                    <tr key={wh.warehouse_id}
                      className={`hover:bg-slate-50 transition-colors ${isInactive ? 'opacity-50 bg-red-50/30' : ''}`}
                      data-testid={`warehouse-row-${wh.warehouse_id}`}>
                      <td className="px-3 py-2 font-mono font-bold text-slate-800 whitespace-nowrap">
                        {wh.warehouse_id}
                        {isInactive && <span className="ml-1.5 text-[10px] font-normal text-red-500 bg-red-50 px-1 py-0.5 rounded">INACTIVE</span>}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-700 max-w-[160px] truncate">{wh.name}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-[140px] truncate">{addr.street || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{addr.city || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{addr.state || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{addr.zip_code || '-'}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{contact.name || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{contact.phone || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-[140px] truncate">{contact.email || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-0.5">
                          {wh.is_active ? (
                            <>
                              <button onClick={() => openEdit(wh)} className="p-1 rounded hover:bg-sky-50 text-sky-600"
                                data-testid={`edit-btn-${wh.warehouse_id}`} title="Edit">
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => setShowDeleteConfirm(wh.warehouse_id)} className="p-1 rounded hover:bg-red-50 text-red-500"
                                data-testid={`delete-btn-${wh.warehouse_id}`} title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <button onClick={() => handleRestore(wh.warehouse_id)} className="p-1 rounded hover:bg-green-50 text-green-600"
                              data-testid={`restore-btn-${wh.warehouse_id}`} title="Restore">
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Warehouse</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Warehouse ID *</Label>
                <Input value={form.warehouse_id} placeholder="e.g. WH-001"
                  onChange={(e) => handleField('warehouse_id', e.target.value.toUpperCase())}
                  data-testid="form-warehouse-id" className="h-8 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Warehouse Name *</Label>
                <Input value={form.name} placeholder="Main Distribution Center"
                  onChange={(e) => handleField('name', e.target.value)}
                  data-testid="form-name" className="h-8 text-sm" />
              </div>
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase pt-1">Address</p>
            {addressFields}
            <p className="text-xs font-bold text-slate-500 uppercase pt-1">Contact Person</p>
            {contactFields}
            <Button onClick={handleCreate} disabled={formLoading}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500" data-testid="form-submit-btn">
              {formLoading ? 'Creating...' : 'Add Warehouse'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Warehouse - {showEdit}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Warehouse Name *</Label>
              <Input value={form.name}
                onChange={(e) => handleField('name', e.target.value)}
                data-testid="edit-name" className="h-8 text-sm" />
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase pt-1">Address</p>
            {addressFields}
            <p className="text-xs font-bold text-slate-500 uppercase pt-1">Contact Person</p>
            {contactFields}
            <Button onClick={handleUpdate} disabled={formLoading}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500" data-testid="edit-submit-btn">
              {formLoading ? 'Updating...' : 'Update Warehouse'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Warehouse</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete warehouse <strong>{showDeleteConfirm}</strong>? Warehouses with stock cannot be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
              Delete Warehouse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default WarehouseMaster;
